<template>
    <v-form v-if="segments.length" ref="form">
        <h4 class="text-subtitle-1 font-weight-bold text-capitalize">
            {{ $t('flights.extra_baggage') }}
        </h4>
        <v-expansion-panels :disabled="disabled" variant="accordion" class="mb-5">
            <v-expansion-panel v-for="segment in segments" :key="segment.rph">
                <v-expansion-panel-title v-slot="{open}">
                    <slot name="segmentTitle" :segment="segment">
                        <div>
                            {{ segment.departure.airportDescription.airportName }},
                            {{ segment.departure.airportDescription.cityName }}
                            ({{ segment.departure.airportCode }}) -&nbsp;
                            {{ segment.arrival.airportDescription.airportName }},
                            {{ segment.arrival.airportDescription.cityName }}
                            ({{ segment.arrival.airportCode }})
                        </div>
                    </slot>
                    <div class="d-flex justify-end mr-5">
                        <v-chip v-if="segmentTotalPrice(segment.rph).amount" color="info" label size="small">
                            +{{ $filters.price(segmentTotalPrice(segment.rph)) }}
                        </v-chip>
                        <span v-else-if="open">
                            {{ $t('flights.select_extra_baggage') }}
                        </span>
                    </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <v-row
                        v-for="extraBaggageOption in specifiedExtraBaggageOptions(segment.rph)"
                        :key="extraBaggageOption.serviceRPH"
                        dense
                    >
                        <v-col cols="12" md="3">
                            <v-checkbox
                                :disabled="disabled || extraBaggageOption.mandatory"
                                :model-value="!!selectedExtraBaggage(extraBaggageOption.serviceRPH)"
                                :label="extraBaggageOption.name"
                                :false-icon="radioMode ? '$radioOff' : '$checkboxOff'"
                                :true-icon="radioMode ? '$radioOn' : '$checkboxOn'"
                                @update:model-value="toggleExtraBaggage(extraBaggageOption)"
                            />
                        </v-col>
                        <v-col cols="12" md="2">
                            <v-text-field
                                :label="$t('price')"
                                :model-value="$filters.price(extraBaggageOption.price)"
                                class="price"
                                disabled
                            />
                        </v-col>
                        <v-col v-if="selectedExtraBaggage(extraBaggageOption.serviceRPH)" cols="12" md="2">
                            <v-text-field
                                :label="$t('quantity')"
                                :max="extraBaggageOption.limit || 5"
                                :min="1"
                                :rules="[
                                    v =>
                                        (!extraBaggageOption.limit && v <= 5 && v > 0) ||
                                        (v <= extraBaggageOption.limit && v > 0),
                                ]"
                                :model-value="
                                    selectedExtraBaggage(extraBaggageOption.serviceRPH)
                                        ? selectedExtraBaggage(extraBaggageOption.serviceRPH).quantity
                                        : 0
                                "
                                type="number"
                                :disabled="extraBaggageOption.mandatory"
                                @update:model-value="changeQuantity(extraBaggageOption.serviceRPH, $event)"
                            />
                        </v-col>
                        <v-col v-if="selectedExtraBaggage(extraBaggageOption.serviceRPH)" cols="12" md="2">
                            <v-text-field
                                :label="$t('total')"
                                :model-value="$filters.price(selectedExtraBaggageTotal(extraBaggageOption))"
                                class="price"
                                disabled
                            />
                        </v-col>
                        <v-col
                            v-if="segmentsCount(extraBaggageOption.serviceRPH) > 1"
                            class="text-caption d-flex align-center"
                            cols="12"
                            md="2"
                        >
                            *{{ $t('flights.for_multiple_flights') }}
                        </v-col>
                    </v-row>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-form>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue} from 'vue-facing-decorator'
    import {clone} from '@/utils/helpers'

    @Component
    export class ExtraBaggageForm extends Vue {
        @VModel() prepareBookRequest
        @Prop({required: true}) index
        @Prop({default: false, type: Boolean}) disabled
        @Prop({required: true}) offer
        @Prop({required: true, default: () => [], type: Array}) extraBaggageOptions

        mounted() {
            if (this.prepareBookRequest.extraBaggage && this.prepareBookRequest.offerKey !== this.offer.offerKey) {
                this.prepareBookRequest.extraBaggage = []
            }

            this.segments.forEach(segment => {
                this.specifiedExtraBaggageOptions(segment.rph).forEach(extraBaggageOption => {
                    if (extraBaggageOption.mandatory) {
                        this.toggleExtraBaggage(extraBaggageOption)
                    }
                })
            })
        }

        specifiedExtraBaggageOptions(rph) {
            return this.extraBaggageOptions.filter(
                extraBaggageOption => !extraBaggageOption.segmentRPHs || extraBaggageOption.segmentRPHs.includes(rph)
            )
        }

        selectedExtraBaggage(serviceRPH) {
            if (!this.prepareBookRequest.extraBaggage) return null
            return this.prepareBookRequest.extraBaggage.find(
                extraBaggage => extraBaggage.touristIndex === this.index && extraBaggage.serviceRPH === serviceRPH
            )
        }

        selectedExtraBaggageTotal(extraBaggageOption) {
            const extraBaggage = this.selectedExtraBaggage(extraBaggageOption.serviceRPH)
            return {
                amount: extraBaggageOption.price.amount * extraBaggage.quantity,
                currency: extraBaggageOption.price.currency,
            }
        }

        toggleExtraBaggage({serviceRPH, segmentRPHs, mandatory}) {
            let extraBaggage = []
            if (this.prepareBookRequest.extraBaggage) {
                extraBaggage = clone(this.prepareBookRequest.extraBaggage)
            }
            const extraBaggageIndex = extraBaggage.findIndex(
                extraBaggage => extraBaggage.touristIndex === this.index && extraBaggage.serviceRPH === serviceRPH
            )
            if (extraBaggageIndex === -1) {
                if (this.radioMode) {
                    const intersection = (a, b) => a.filter(x => b.includes(x))
                    extraBaggage = extraBaggage.filter(
                        extraBaggage =>
                            extraBaggage.touristIndex !== this.index ||
                            !intersection(
                                segmentRPHs,
                                this.extraBaggageOptions.find(({serviceRPH}) => extraBaggage.serviceRPH === serviceRPH)
                                    .segmentRPHs
                            ).length
                    )
                }
                extraBaggage.push({
                    serviceRPH,
                    quantity: 1,
                    touristIndex: this.index,
                })
            } else if (!mandatory) {
                extraBaggage.splice(extraBaggageIndex, 1)
            }
            if (this.prepareBookRequest.offerKey !== this.offer.offerKey) {
                this.prepareBookRequest.offerKey = this.offer.offerKey
            }
            this.prepareBookRequest.extraBaggage = extraBaggage
        }

        changeQuantity(serviceRPH, quantity) {
            const extraBaggage = clone(this.prepareBookRequest.extraBaggage)
            const extraBaggageIndex = this.prepareBookRequest.extraBaggage.findIndex(
                extraBaggage => extraBaggage.touristIndex === this.index && extraBaggage.serviceRPH === serviceRPH
            )
            extraBaggage[extraBaggageIndex].quantity = +quantity
            this.prepareBookRequest.extraBaggage = extraBaggage
        }

        segmentTotalPrice(rph) {
            const segmentTotalPrice = {amount: 0, currency: persistentStore.currency}
            if (!this.prepareBookRequest.extraBaggage) return segmentTotalPrice
            return this.prepareBookRequest.extraBaggage.reduce((segmentTotalPrice, extraBaggage) => {
                if (extraBaggage.touristIndex !== this.index) return segmentTotalPrice
                const extraBaggageOption = this.specifiedExtraBaggageOptions(rph).find(
                    extraBaggageOption => extraBaggage.serviceRPH === extraBaggageOption.serviceRPH
                )
                if (
                    !extraBaggageOption ||
                    !extraBaggageOption.price ||
                    (extraBaggageOption.segmentRPHs && extraBaggageOption.segmentRPHs[0] !== rph)
                )
                    return segmentTotalPrice
                segmentTotalPrice.amount +=
                    this.$filters.convertPrice(extraBaggageOption.price).amount * extraBaggage.quantity
                return segmentTotalPrice
            }, segmentTotalPrice)
        }

        segmentsCount(serviceRPH) {
            const extraBaggageOption = this.extraBaggageOptions.find(
                extraBaggageOption => extraBaggageOption.serviceRPH === serviceRPH
            )
            return extraBaggageOption.segmentRPHs ? extraBaggageOption.segmentRPHs.length : 1
        }

        get segments() {
            return this.offer.itinerary.reduce((segments, itinerary) => {
                itinerary.segments.forEach(segment => {
                    if (this.specifiedExtraBaggageOptions(segment.rph).length) {
                        segments.push(segment)
                    }
                })
                return segments
            }, [])
        }

        get radioMode() {
            return this.offer.supplierName === 'Travelfusion'
        }
    }

    export default ExtraBaggageForm
</script>
